import React, { useState } from 'react';
import {
  Box,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  TextField
} from '@mui/material';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { WalteroCard, WalteroTypography } from '../waltero';

const ApiUsageTrends = ({ data, dateRange, onDateRangeChange, onGranularityChange }) => {
  const [granularity, setGranularity] = useState('day');
  const [chartType, setChartType] = useState('line');

  const handleGranularityChange = (event, newGranularity) => {
    if (newGranularity) {
      setGranularity(newGranularity);
      onGranularityChange(newGranularity);
    }
  };

  const handleChartTypeChange = (event, newType) => {
    if (newType) {
      setChartType(newType);
    }
  };

  const chartData = data?.trends || [];

  const ChartComponent = chartType === 'line' ? LineChart : BarChart;
  const DataComponent = chartType === 'line' ? Line : Bar;

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Controls */}
        <Grid item xs={12}>
          <WalteroCard>
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Start Date"
                    value={dateRange.startDate}
                    onChange={(e) => onDateRangeChange({ ...dateRange, startDate: e.target.value })}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="End Date"
                    value={dateRange.endDate}
                    onChange={(e) => onDateRangeChange({ ...dateRange, endDate: e.target.value })}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <ToggleButtonGroup
                    value={granularity}
                    exclusive
                    onChange={handleGranularityChange}
                    size="small"
                  >
                    <ToggleButton value="day">Daily</ToggleButton>
                    <ToggleButton value="month">Monthly</ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
                <Grid item xs={12} md={3}>
                  <ToggleButtonGroup
                    value={chartType}
                    exclusive
                    onChange={handleChartTypeChange}
                    size="small"
                  >
                    <ToggleButton value="line">Line</ToggleButton>
                    <ToggleButton value="bar">Bar</ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </Grid>
            </Box>
          </WalteroCard>
        </Grid>

        {/* API Calls Trend */}
        <Grid item xs={12}>
          <WalteroCard>
            <Box sx={{ p: 2 }}>
              <WalteroTypography variant="h6" gutterBottom>
                API Calls Over Time
              </WalteroTypography>
              <ResponsiveContainer width="100%" height={300}>
                <ChartComponent data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <DataComponent
                    type="monotone"
                    dataKey="total_calls"
                    stroke="#094872"
                    fill="#094872"
                    name="Total Calls"
                  />
                  <DataComponent
                    type="monotone"
                    dataKey="successful_calls"
                    stroke="#4CAF50"
                    fill="#4CAF50"
                    name="Successful"
                  />
                  <DataComponent
                    type="monotone"
                    dataKey="failed_calls"
                    stroke="#f44336"
                    fill="#f44336"
                    name="Failed"
                  />
                </ChartComponent>
              </ResponsiveContainer>
            </Box>
          </WalteroCard>
        </Grid>

        {/* Users and Success Rate Trend */}
        <Grid item xs={12} md={6}>
          <WalteroCard>
            <Box sx={{ p: 2 }}>
              <WalteroTypography variant="h6" gutterBottom>
                Unique Users Trend
              </WalteroTypography>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="unique_users"
                    stroke="#c35d2c"
                    name="Unique Users"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </WalteroCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <WalteroCard>
            <Box sx={{ p: 2 }}>
              <WalteroTypography variant="h6" gutterBottom>
                Success Rate Trend
              </WalteroTypography>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip formatter={(value) => `${value}%`} />
                  <Line
                    type="monotone"
                    dataKey="success_rate"
                    stroke="#4CAF50"
                    name="Success Rate"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="error_rate"
                    stroke="#f44336"
                    name="Error Rate"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </WalteroCard>
        </Grid>

        {/* Summary Statistics */}
        {data?.summary && (
          <Grid item xs={12}>
            <WalteroCard>
              <Box sx={{ p: 2 }}>
                <WalteroTypography variant="h6" gutterBottom>
                  Period Summary
                </WalteroTypography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <WalteroTypography variant="caption" color="textSecondary">
                      Total API Calls
                    </WalteroTypography>
                    <WalteroTypography variant="h6">
                      {new Intl.NumberFormat().format(data.summary.totalCalls)}
                    </WalteroTypography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <WalteroTypography variant="caption" color="textSecondary">
                      Daily Average
                    </WalteroTypography>
                    <WalteroTypography variant="h6">
                      {new Intl.NumberFormat().format(data.summary.averageCallsPerPeriod)}
                    </WalteroTypography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <WalteroTypography variant="caption" color="textSecondary">
                      Peak Calls
                    </WalteroTypography>
                    <WalteroTypography variant="h6">
                      {new Intl.NumberFormat().format(data.summary.peakCalls)}
                    </WalteroTypography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <WalteroTypography variant="caption" color="textSecondary">
                      Unique Users
                    </WalteroTypography>
                    <WalteroTypography variant="h6">
                      {data.summary.totalUniqueUsers}
                    </WalteroTypography>
                  </Grid>
                </Grid>
              </Box>
            </WalteroCard>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ApiUsageTrends;